import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { BasicDetailsForm } from "entities/RequestBasicDetails";
import constants from "entities/RequestBasicDetails/constants";
import { useRequestDependencies } from "entities/Request";
import { useRequestStore } from "entities/Request/store/useRequestStore";
import { RequestAccessController } from "entities/Request/utils/RequestAccessController";
import { getBasicDetailsValidationSchema } from "features/EditRequestBasicDetails/lib/validations/basicDetailsSchema";
import { ChangeTravelAcceptanceStatus } from "features/ChangeTravelAcceptanceStatus";
import { SelectAccomodation } from "features/SelectAccomodation";
import SelectInternalOrder from "features/SelectInternalOrder/ui/SelectInternalOrder/SelectInternalOrder";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Col, Stack } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Icons from "shared/icons";
import { Button } from "shared/ui";
import { BasicDetailsContainer } from "widgets/Request";
import { REQUEST_TYPES, ROLES } from "shared/config";
import {
  useAdditionalAcceptorField,
  useAdvancePaymentFieldController,
} from "features/EditRequestBasicDetails";

const { defaultValues } = constants.basicDetails;
const access = new RequestAccessController();

const EditRequestBasicDetails = forwardRef(
  (
    {
      // @ts-ignore
      formHeader,
      // @ts-ignore
      representativeFullName,
      // @ts-ignore
      requestorFullName,
      // @ts-ignore
      requestPlacedBy,
      // @ts-ignore
      request,
      // @ts-ignore
      userRole,
      // @ts-ignore
      advancePaymentsQueue,
      // @ts-ignore
      withInternalOrders = false,
      // @ts-ignore
      withAccomodation = false,
      // @ts-ignore
      validations,
      // @ts-ignore
      dependencies,
      // @ts-ignore
      isAdvancePaymentAllowed,
      // @ts-ignore
      totalAmount,
      // @ts-ignore
      disableInternalOrders = false,
    },
    ref
  ) => {
    const { t } = useTranslation("RequestBasicDetails");
    const navigate = useNavigate();
    const setRequestBasicDetailsToUpdate = useRequestStore(
      (state) => state.setRequestBasicDetailsToUpdate
    );

    const form = useForm({
      defaultValues,
      resolver: yupResolver(
        getBasicDetailsValidationSchema(
          validations?.advance_payment_rules?.minimum_advance_amount,
          request?.type
        )
      ),
    });

    const {
      data: { dependencies: travelAcceptorDependencies } = {},
      isLoading: isLoadingTravelAcceptorDependencies,
    } = useRequestDependencies("travel_acceptor_form", request?.id, {
      triggerRequest: userRole === ROLES.ADMINISTRATION,
    });

    const administrativeUsersOptions = useMemo(
      () =>
        travelAcceptorDependencies?.list_administration_users?.data.map(
          (user) => ({
            value: user.id,
            label: user.first_name + " " + user.last_name,
          })
        ),
      [travelAcceptorDependencies]
    );

    const cwtStatusesOptions = useMemo(() => {
      if (!travelAcceptorDependencies?.available_cwt_statuses?.data) return;

      return Object.entries(
        travelAcceptorDependencies?.available_cwt_statuses?.data
        // eslint-disable-next-line no-unused-vars
      )?.map(([_, value]) => ({ value: value, label: value }));
    }, [travelAcceptorDependencies]);

    const {
      reset,
      control,
      handleSubmit,
      register,
      getValues,
      setError,
      formState: { errors, isDirty },
    } = form;

    const resetForm = () => {
      reset(
        Object.fromEntries(
          Object.entries(defaultValues).map(([key, value]) => {
            let newValue = request?.[key] ?? value;
            if (key === "attachments") {
              newValue = request?.[key]?.data?.map((fileData) => fileData.id);
            }
            /*
            "representation_id" field is required for supervisor and additional acceptor fields validation 
            and must be an object with the value, and search_string: request.requestor_email
          */
            if (key === "representation_id") {
              newValue = {
                value: request.representation_id ?? null,
                search_string: request.requestor_email,
              };
            }
            if (key === "advance_payment") {
              newValue = request?.advance_payments?.data[0]?.amount ?? null;
            }
            if (key === "person_responsible") {
              newValue = request?.travel_cwt_declaration?.data
                ?.administration_user_id
                ? {
                    value:
                      request?.travel_cwt_declaration?.data
                        ?.administration_user_id,
                    label:
                      request?.travel_cwt_declaration?.data
                        ?.administration_name,
                  }
                : null;
            }
            if (key === "status") {
              newValue = request?.travel_cwt_declaration?.data?.status
                ? {
                    value: request?.travel_cwt_declaration?.data?.status,
                    label: request?.travel_cwt_declaration?.data?.status,
                  }
                : null;
            }
            return [key, newValue];
          })
        )
      );
    };

    useEffect(() => {
      if (request?.advance_payments?.data?.length) {
        setAdvancePayment(true);
      }
      resetForm();
    }, [request]);

    const isRequestEditable = access
      .to("BasicDetails", "edit")
      .withHold()
      .withLock()
      .provided();

    useImperativeHandle(
      ref,
      () => ({
        submit() {
          const accomodationOptionIsNotSelected =
            withAccomodation && !getValues("accommodation_payment_type");

          setRequestBasicDetailsToUpdate(
            new Promise((resolve) => {
              handleSubmit(
                // @ts-ignore
                ({ representation_id, advance_payment, ...formData }) => {
                  if (accomodationOptionIsNotSelected) {
                    setError("accommodation_payment_type", {
                      type: "custom",
                      message: t(
                        "validations.accommodation_payment_type.is_required"
                      ),
                    });
                  }
                  resolve({
                    isValid: !accomodationOptionIsNotSelected,
                    isDirty,
                    formData: {
                      user_role: userRole,
                      representation_id: representation_id?.value,
                      // Back requires amount as a string ¯\_(ツ)_/¯
                      advance_payment:
                        typeof advance_payment === "number"
                          ? advance_payment.toString()
                          : null,
                      ...formData,
                    },
                  });
                },
                (errors) => resolve({ isValid: false, errors })
              )();
            })
          );
        },
      }),
      [isDirty, withAccomodation]
    );

    const uploadedFiles = useMemo(
      () =>
        request.attachments?.data.map((attachment) => ({
          ...attachment,
          isUploaded: true,
        })),
      [request.attachments?.data]
    );

    const [advancePayment, setAdvancePayment] = useState(false);

    const toggleAdvancePayment = useCallback(() => {
      setAdvancePayment(!advancePayment);
    }, [advancePayment]);

    const requestorEmail = request?.requestor_email;

    const {
      additionalAcceptorFieldState,
      setAdditionalAcceptorFieldState,
      toggleAdditionalAcceptorField,
    } = useAdditionalAcceptorField(
      form,
      dependencies,
      advancePayment,
      requestorEmail
    );

    useAdvancePaymentFieldController(
      form,
      validations,
      dependencies,
      advancePayment,
      setAdditionalAcceptorFieldState,
      requestorEmail
    );

    // TODO Refactor to access controller

    const isCostCenterDisabled =
      request?.type === REQUEST_TYPES.EUROCARD &&
      userRole !== ROLES.ACCOUNTS_PAYABLE;

    const isSupervisorDisabled =
      advancePayment ||
      (request?.type === REQUEST_TYPES.EUROCARD &&
        userRole !== ROLES.ACCOUNTS_PAYABLE) ||
      (request?.type === REQUEST_TYPES.AMEX_BTA &&
        userRole !== ROLES.LOCAL_ADMINISTRATOR) ||
      (request?.type === REQUEST_TYPES.CREDIT_CARD &&
        userRole !== ROLES.LOCAL_ADMINISTRATOR);

    return (
      <BasicDetailsContainer>
        <BasicDetailsForm
          form={form}
          requestType={request?.type}
          formHeader={formHeader}
          validations={validations}
          advancePaymentCurrencyCode={
            request?.advance_payments?.data?.[0]?.currency_code ??
            validations?.advance_payment_rules?.currency_code
          }
          disabled={!isRequestEditable}
          displayAdvancePayment={isAdvancePaymentAllowed}
          disableAdvancePayment={access
            .to("AdvancePayment", "edit")
            .unprovided()}
          disableCostCenter={isCostCenterDisabled}
          uploadedFiles={uploadedFiles}
          // TODO change props name
          accountsPayableSpecificInfo={
            <>
              {userRole === ROLES.ACCOUNTS_PAYABLE && (
                <small className="d-block">
                  {t("labels.employee_sap_number")}
                  <strong>{request?.sap_vendor_number}</strong>
                </small>
              )}
              {!advancePaymentsQueue && totalAmount && (
                <small className="d-block">
                  <Trans i18nKey="total_amount">
                    {t("labels.total_amount")} <strong>{totalAmount}</strong>
                  </Trans>
                </small>
              )}
            </>
          }
          representativeFullName={representativeFullName}
          requestorFullName={requestorFullName}
          requestPlacedBy={requestPlacedBy}
          vipRequestor={request?.is_vip && userRole === ROLES.ACCOUNTS_PAYABLE}
          toggleAdditionalAcceptorField={toggleAdditionalAcceptorField}
          displayAdditionalAcceptance={request?.type !== REQUEST_TYPES.EUROCARD}
          additionalAcceptance={additionalAcceptorFieldState.open}
          lockAdditionalAcceptance={additionalAcceptorFieldState.disabled}
          toggleAdvancePayment={toggleAdvancePayment}
          advancePayment={advancePayment}
          requestStatus={request.status}
          requestPreviousAction={request?.previous_action}
          isSupervisorDisabled={isSupervisorDisabled}
          selectInternalOrders={
            withInternalOrders && (
              <SelectInternalOrder
                isSearchable={false}
                userRole={userRole}
                isDisabled={!isRequestEditable || disableInternalOrders}
                label={t("labels.internal_order")}
                displayAs={Col}
              />
            )
          }
          accomodationOptions={
            withAccomodation && (
              <SelectAccomodation
                control={control}
                register={register}
                error={errors.accommodation_payment_type}
                disabled={!request.is_accommodation_editable}
              />
            )
          }
          formControls={
            <Stack direction="horizontal" className="justify-content-between">
              <Button onClick={() => navigate(-1)} variant="outline-primary">
                <FontAwesomeIcon icon={Icons.arrowLeft} className="me-2" />
                {t("buttons.back")}
              </Button>
            </Stack>
          }
          travelAcceptorControls={
            userRole === ROLES.ADMINISTRATION && (
              <ChangeTravelAcceptanceStatus
                administrativeUsersOptions={administrativeUsersOptions}
                loadingTravelAcceptorDependencies={
                  isLoadingTravelAcceptorDependencies
                }
                cwtStatusesOptions={cwtStatusesOptions}
                control={control}
                handleSubmit={handleSubmit}
                errors={errors}
                form={form}
              />
            )
          }
        />
      </BasicDetailsContainer>
    );
  }
);

export default memo(EditRequestBasicDetails);
